/* eslint-disable max-lines */
import { renderMarkdown } from './SkillsAssistant/renderMarkdown';
import DCAIcon from '../../../../../assets/icons/GenAi/dca-icon.svg';
import NoData from '../../../../../assets/icons/GenAi/no-data.svg';
import NoRecord from '../../../../../assets/icons/GenAi/no-record.svg';
import FileUpload from '../../../../../assets/icons/GenAi/file-upload.svg';
import PDFFile from '../../../../../assets/icons/GenAi/pdf-file-icon.png';
import { STORAGE_CONSTANTS, storage } from '../../../../../libs/services/LocalStorage';
import {
  Button,
  Col,
  Card,
  Divider,
  Drawer,
  Row,
  Typography,
  message,
  Popover,
  Tabs,
  Modal,
  Upload,
} from 'antd';
import { isEmpty } from 'lodash';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { RcFile } from 'antd/lib/upload';
import { CloseCircleOutlined, CloudUploadOutlined, PlusCircleFilled } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import CloseIcon from '../../../../../assets/icons/close-icon.svg';
import './DrawerOverHead.less';
import {
  ICreateSessionPayload,
  IArchiveChatPayload,
  IUploadGenAiDocumentPayload,
  RoutingMode,
} from '../../../../../libs/models/GenAIModels';
import { GenAIActions } from '../../../../../libs/store/actions/GenAIActions';
import { IApplicationState, useAppSelector } from '../../../../../libs/store/reducers';
import Copy from '../../../../../assets/icons/GenAi/copy.svg';
import Regenerate from '../../../../../assets/icons/GenAi/regenerate.svg';
import Loader from '../../../../../components/ui/Loader/Loader';
import SearchBox from './SearchBox';
import GenAiTabs from './GenAiTabs';
import ChatSessionOptions from './ChatSessionOptions';
import SkillsAssistant from './SkillsAssistant/SkillsAssistant';
import CopilotAgents from '../../../../../pages/Operate/CopilotAgents/CopilotAgents';
import { consumePendingTab } from '../../../../../libs/utils/copilotSignal';
import { AVAILABLE_MODELS } from '../../../../../constants/AvailableModels';

type CopilotMode = 'CopilotQueries' | 'DocumentAnalysis' | 'SkillsAssistant' | 'Agents';

interface IDrawerOverHeadProps {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  query: string;
  cardContent?: string;
  setCardContent: React.Dispatch<React.SetStateAction<string | undefined>>;
  globalSearch: boolean;
  setIsGlobalSearch: React.Dispatch<React.SetStateAction<boolean>>;
}

const DrawerOverHead: React.FC<IDrawerOverHeadProps> = ({
  visible,
  setVisible,
  setQuery,
  query,
  cardContent,
  setCardContent,
  globalSearch,
  setIsGlobalSearch,
}) => {
  const userId = storage.getItem(STORAGE_CONSTANTS.userId)!;
  const [selectedSearchHistoryId, setSelectedSearchHistoryId] = useState<string>();
  const [activeTab, setActiveTab] = useState(0);
  const [activePopover, setActivePopover] = useState<string>();
  const [isKeywordSearch, setIsKeywordSearch] = useState<boolean>(true);
  const [copilotMode, setCopilotMode] = useState<CopilotMode>('CopilotQueries');
  const [isUploadOpen, setIsUploadOpen] = useState<boolean>(false);
  const [uploadFile, setUploadFile] = useState<RcFile>();
  const [isNewSession, setIsNewSession] = useState<boolean>(false);
  const [selectedModel, setSelectedModel] = useState<string>(AVAILABLE_MODELS[0].id);
  const [routingMode, setRoutingMode] = useState<RoutingMode>('auto');
  const [disableCache, setDisableCache] = useState<boolean>(false);
  const chatBodyRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();
  const { isLoading, recentSearchList, currentChatSession } = useAppSelector(
    ({ genAI: { loading, loadingCount, recentSearchList, currentChatSession } }: IApplicationState) => ({
      isLoading: loading || loadingCount !== 0,
      recentSearchList,
      currentChatSession,
    }),
  );

  const handleSearchList = () => {
    setIsNewSession(true);
    setSelectedSearchHistoryId(undefined);
    setCardContent(undefined);
    setIsUploadOpen(!isKeywordSearch);
  };

  const dispatcher = {
    getRecentSearch: () => dispatch(GenAIActions.fetchRecentSearchListStart({ userId })),
    createOrUpdateGlobalSession: (payload: ICreateSessionPayload) =>
      dispatch(GenAIActions.currentChatSessionStart(payload)),
    archiveChat: (payload: IArchiveChatPayload) => dispatch(GenAIActions.archiveChatStart(payload)),
    uploadDocument: (payload: IUploadGenAiDocumentPayload) =>
      dispatch(GenAIActions.uploadDocumentStart(payload)),
  };

  const onCopySuccess = async () => {
    await message.success('Copied to clipboard.', 3);
  };
  useEffect(() => {
    if (!visible) return;
    const pendingTab = consumePendingTab();
    if (pendingTab) {
      setCopilotMode(pendingTab as CopilotMode);
      setIsNewSession(false);
      setSelectedSearchHistoryId(userId + '_global');
      return;
    }
    if (globalSearch) {
      setIsKeywordSearch(true);
      setCopilotMode('CopilotQueries');
      setActiveTab(0);
      dispatcher.createOrUpdateGlobalSession({
        userId,
        query: cardContent || '',
        sessionId: userId + '_global',
        isDocSearch: false,
        model: routingMode === 'manual' ? selectedModel : undefined,
        routingMode,
        disableCache,
      });
    } else {
      dispatcher.getRecentSearch();
    }
    setIsNewSession(false);
    setSelectedSearchHistoryId(userId + '_global');
  }, [visible]);

  useEffect(() => {
    if (currentChatSession?.session_id && !selectedSearchHistoryId) {
      setIsNewSession(false);
      setSelectedSearchHistoryId(currentChatSession.session_id);
    }
  }, [currentChatSession]);

  const sortedListAndSelectedSearchItem = useMemo(() => {
    let selectedRecentSearchItem = undefined;
    const filteredRecentSearchList = recentSearchList.filter(({ isDocumentSearch }) =>
      isKeywordSearch ? !isDocumentSearch : isDocumentSearch,
    );
    if (!filteredRecentSearchList) {
      return {
        selectedRecentSearchItem,
        sortedList: [],
        nonArchivedLength: 0,
        archivedLength: 0,
      };
    }

    const nonArchivedList = filteredRecentSearchList.filter(
      (searchedItem) => !searchedItem.archived,
    );
    const archivedList = filteredRecentSearchList.filter((searchedItem) => searchedItem.archived);
    const selectedList = activeTab === 0 ? nonArchivedList : archivedList;

    const sortedList = [...selectedList].sort((searchItemA, searchItemB) => {
      if (searchItemA.sessionId === userId + '_global') {
        return -1;
      }
      if (searchItemB.sessionId === userId + '_global') {
        return 1;
      }
      return new Date(searchItemB.updatedAt).getTime() - new Date(searchItemA.updatedAt).getTime();
    });

    if (!isEmpty(selectedSearchHistoryId)) {
      selectedRecentSearchItem = selectedList.find(
        (recentSearch) => recentSearch.sessionId === selectedSearchHistoryId,
      );
    }

    return {
      selectedRecentSearchItem,
      sortedList,
      nonArchivedLength: nonArchivedList.length,
      archivedLength: archivedList.length,
    };
  }, [recentSearchList, selectedSearchHistoryId, activeTab, isKeywordSearch, isLoading, userId]);

  useEffect(() => {
    if (isLoading || isNewSession) return;
    if (isEmpty(sortedListAndSelectedSearchItem.selectedRecentSearchItem)) {
      const first = sortedListAndSelectedSearchItem.sortedList[0]?.sessionId;
      setSelectedSearchHistoryId(first ?? undefined);
    }
  }, [sortedListAndSelectedSearchItem, isLoading, isNewSession]);

  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [sortedListAndSelectedSearchItem.selectedRecentSearchItem?.queryList, isLoading]);

  const tabs = [
    `${isKeywordSearch ? 'Recent' : 'Uploads'} (${sortedListAndSelectedSearchItem.nonArchivedLength})`,
    `Archived (${sortedListAndSelectedSearchItem?.archivedLength})`,
  ];

  const renderDrawerHeader = () => {
    return (
      <>
        <span>{'DCA Copilot'}</span>
        <Tabs
          className="header-tabs"
          activeKey={copilotMode}
          onChange={(key) => {
            const mode = key as CopilotMode;
            setCopilotMode(mode);
            if (mode !== 'SkillsAssistant') {
              setIsKeywordSearch(mode === 'CopilotQueries');
            }
          }}
        >
          <Tabs.TabPane tab={'Copilot Queries'} key={'CopilotQueries'}></Tabs.TabPane>
          <Tabs.TabPane tab={'Document Analysis'} key={'DocumentAnalysis'}></Tabs.TabPane>
          <Tabs.TabPane tab={'Skills Assistant'} key={'SkillsAssistant'}></Tabs.TabPane>
          <Tabs.TabPane tab={'Agents'} key={'Agents'}></Tabs.TabPane>
        </Tabs>
      </>
    );
  };

  const handleUploadFile = () => {
    const blob = new Blob([uploadFile!], { type: uploadFile!.type });
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    })
      .then((text) => {
        dispatcher.uploadDocument({
          userId,
          fileName: uploadFile!.name,
          inputFile: text.replace('data:application/pdf;base64,', ''),
        });
      })
      .finally(() => {
        setIsUploadOpen(false);
        setUploadFile(undefined);
      });
  };

  return (
    <Drawer
      className="Gen-AI-drawer"
      title={renderDrawerHeader()}
      placement="right"
      width="100%"
      footer={null}
      closable={true}
      onClose={() => {
        setIsGlobalSearch(true);
        setVisible(false);
        setQuery('');
        setSelectedSearchHistoryId(undefined);
      }}
      open={visible}
      closeIcon={<img src={CloseIcon} alt="close" className="close-icon" />}
    >
      <Loader loading={false} loaderName={''}>
        {copilotMode === 'Agents' ? (
          // Also carries `drawer-container` (not just the `--agents` modifier)
          // so shared rules scoped to `.Gen-AI-drawer .drawer-container ...`
          // (e.g. .model-chip, .chat-panel__header) reach this subtree too —
          // `--agents` is a sibling class here, not an extension of it.
          <div className="drawer-container drawer-container--agents">
            <CopilotAgents embedded open onClose={() => {}} />
          </div>
        ) : copilotMode === 'SkillsAssistant' ? (
          <div className="drawer-container drawer-container--skills-assistant">
            <SkillsAssistant />
          </div>
        ) : (
        <Row className="drawer-container">
          <Col span={5}>
            <Card bordered={true} className="recent-searches-card">
              <GenAiTabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
              <Divider />
              <div className="flex-column h-100 justify-space-between">
                {sortedListAndSelectedSearchItem.sortedList.length ? (
                  <div className="recent-searches-sidebar">
                    <div className="recent-search-body">
                      {sortedListAndSelectedSearchItem.sortedList.map((item) => (
                        <span
                          key={item.sessionId}
                          className={`chat-preview ${
                            item.sessionId === selectedSearchHistoryId ? 'selected' : ''
                          }`}
                          onClick={() => { setIsNewSession(false); setSelectedSearchHistoryId(item.sessionId); }}
                        >
                          <span className="session-name">
                            {isKeywordSearch ? (
                              item.queryList.slice(-1)[0]?.question
                            ) : (
                              <>
                                <img src={PDFFile} alt={'PDF'} />
                                {item.sessionName}
                              </>
                            )}
                          </span>
                          <Popover
                            placement="bottomLeft"
                            content={
                              <ChatSessionOptions
                                session={item}
                                archiveChat={dispatcher.archiveChat}
                                hidePopover={() => setActivePopover(undefined)}
                              />
                            }
                            trigger="click"
                            open={activePopover === item.sessionId}
                            onOpenChange={(viviblePopover) =>
                              setActivePopover(viviblePopover ? item.sessionId : undefined)
                            }
                            zIndex={100}
                            arrowPointAtCenter
                            className="popover"
                          >
                            <span className="hover-img">{'•••'}</span>
                          </Popover>
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="no-record">
                    <img src={NoRecord} alt="no record" />
                    <Typography>{`No ${
                      isKeywordSearch ? 'records' : 'documents'
                    } available.`}</Typography>
                  </div>
                )}
                <Button className="new-session-button" onClick={handleSearchList}>
                  <Typography className="select-button-text">
                    {isKeywordSearch ? (
                      <>
                        <PlusCircleFilled /> {'Start New Session'}
                      </>
                    ) : (
                      <>
                        <img src={FileUpload} alt={'Upload File'} /> {'Upload Document'}
                      </>
                    )}
                  </Typography>
                </Button>
              </div>
              <Modal
                title="Upload Document"
                open={isUploadOpen}
                centered
                className="upload-file-modal"
                onCancel={() => setIsUploadOpen(false)}
                closeIcon={<CloseCircleOutlined />}
                footer={
                  uploadFile !== undefined ? (
                    <Button className="upload-file-button" onClick={handleUploadFile}>
                      {'Save'}
                    </Button>
                  ) : null
                }
              >
                <Upload.Dragger
                  name={'file'}
                  maxCount={1}
                  fileList={uploadFile ? [uploadFile] : []}
                  beforeUpload={async (file) => {
                    const isPDF = file.type === 'application/pdf';
                    if (!isPDF) {
                      await message.error(`${file.name} is not a pdf file`);
                    } else {
                      setUploadFile(file);
                    }
                    return false;
                  }}
                  progress={{
                    strokeColor: '#86BC26',
                    strokeWidth: 10,
                    format: (percent) => percent && `${parseFloat(percent.toFixed(2))}%`,
                  }}
                  iconRender={() => <img src={PDFFile} alt={'PDF'} />}
                  onRemove={() => setUploadFile(undefined)}
                  showUploadList={{
                    showRemoveIcon: true,
                    removeIcon: 'Remove',
                  }}
                >
                  <CloudUploadOutlined />
                  {'Drop File here, or'}&nbsp;
                  <Typography.Link>{'Browse'}</Typography.Link>
                </Upload.Dragger>
              </Modal>
            </Card>
          </Col>
          <Col className="chat-panel" span={19}>
            {/* Chat header: title + model selector */}
            <div className="chat-panel__header">
              <div className="chat-panel__header-left">
                <img src={DCAIcon} alt="DCA" className="chat-panel__header-icon" />
                <div>
                  <div className="chat-panel__header-title">
                    {isKeywordSearch ? 'Copilot Queries' : 'Document Analysis'}
                  </div>
                  <div className="chat-panel__header-sub">
                    <span className="chat-online-dot" />
                    {'Powered by DCA AI'}
                  </div>
                </div>
              </div>
              <div className="chat-panel__model-bar">
                <span className="chat-model-label">Model</span>
                <button
                  type="button"
                  className={`model-chip model-chip--auto${routingMode === 'auto' ? ' active' : ''}`}
                  onClick={() => setRoutingMode('auto')}
                  title="Automatically route to the most cost-optimized model/cache that can answer this query"
                >
                  {'Auto'}
                </button>
                {/* olmo-3-7b excluded here deliberately — it's scoped to the
                    Scientific Lit. FAQ agent (ModelBar's allowedModels) only,
                    not this generic, not-per-agent Copilot Queries drawer. */}
                {AVAILABLE_MODELS.filter((m) => m.id !== 'olmo-3-7b').map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className={`model-chip${
                      routingMode === 'manual' && selectedModel === m.id ? ' active' : ''
                    }`}
                    onClick={() => {
                      setSelectedModel(m.id);
                      setRoutingMode('manual');
                    }}
                    title={m.provider}
                  >
                    {m.label}
                  </button>
                ))}
                <label className="chat-disable-cache" title="Skip the response cache and always call the model">
                  <input
                    type="checkbox"
                    checked={disableCache}
                    onChange={(e) => setDisableCache(e.target.checked)}
                  />
                  {'Disable cache'}
                </label>
              </div>
            </div>

            {/* Chat messages body */}
            <div className="chat-panel__body" ref={chatBodyRef}>
              {sortedListAndSelectedSearchItem.selectedRecentSearchItem ? (
                <>
                  {sortedListAndSelectedSearchItem.selectedRecentSearchItem.queryList.map(
                    (qa, i) => (
                      <React.Fragment key={i}>
                        <div className="chat-row chat-row--user">
                          <div className="chat-bubble chat-bubble--user">{qa.question}</div>
                        </div>
                        <div className="chat-row chat-row--assistant">
                          <img src={DCAIcon} alt="DCA" className="chat-avatar" />
                          <div className="chat-bubble-wrap">
                            <div className="chat-bubble chat-bubble--assistant">
                              <div className="chat-bubble__text chat-bubble__text--md">{renderMarkdown(qa.answer)}</div>
                              {(qa.model || qa.tier || qa.cacheHit) && (
                                <div className="chat-bubble__tags">
                                  {qa.cacheHit && (
                                    <span className="chat-bubble__badge chat-bubble__badge--cache">Cached</span>
                                  )}
                                  {qa.tier && !qa.cacheHit && (
                                    <span className="chat-bubble__badge chat-bubble__badge--tier">{qa.tier}</span>
                                  )}
                                  {qa.model && (
                                    <span className="chat-bubble__badge chat-bubble__badge--model">{qa.model}</span>
                                  )}
                                  {typeof qa.cost === 'number' && (
                                    <span className="chat-bubble__badge chat-bubble__badge--cost">
                                      {`$${qa.cost.toFixed(6)}`}
                                    </span>
                                  )}
                                </div>
                              )}
                              <div className="chat-bubble__actions">
                                <CopyToClipboard text={qa.answer} onCopy={onCopySuccess}>
                                  <Button size="small" type="link" className="chat-action-btn">
                                    <img src={Copy} alt="copy" />
                                    <span>Copy</span>
                                  </Button>
                                </CopyToClipboard>
                                <Button
                                  size="small"
                                  type="link"
                                  className="chat-action-btn"
                                  onClick={() => {
                                    dispatcher.createOrUpdateGlobalSession({
                                      userId,
                                      query: qa.question,
                                      sessionId: selectedSearchHistoryId,
                                      isDocSearch: !isKeywordSearch,
                                      model: routingMode === 'manual' ? selectedModel : undefined,
                                      routingMode,
                                      disableCache,
                                    });
                                  }}
                                >
                                  <img src={Regenerate} alt="regenerate" />
                                  <span>Regenerate</span>
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    ),
                  )}
                  {isLoading && (
                    <div className="chat-row chat-row--assistant">
                      <img src={DCAIcon} alt="DCA" className="chat-avatar" />
                      <div className="chat-bubble chat-bubble--assistant chat-typing-bubble">
                        <span className="chat-dot-anim" />
                        <span className="chat-dot-anim" />
                        <span className="chat-dot-anim" />
                      </div>
                    </div>
                  )}
                </>
              ) : isLoading ? (
                <div className="chat-row chat-row--assistant">
                  <img src={DCAIcon} alt="DCA" className="chat-avatar" />
                  <div className="chat-bubble chat-bubble--assistant chat-typing-bubble">
                    <span className="chat-dot-anim" />
                    <span className="chat-dot-anim" />
                    <span className="chat-dot-anim" />
                  </div>
                </div>
              ) : (
                <div className="chat-empty">
                  <img src={NoData} alt="empty" className="chat-empty__img" />
                  <div className="chat-empty__title">
                    {isKeywordSearch
                      ? 'Ask your first question'
                      : 'Upload a document to get started'}
                  </div>
                  <div className="chat-empty__sub">
                    {isKeywordSearch
                      ? 'Select a model above, then type your query below.'
                      : 'Upload a document using the button on the left, then ask questions here.'}
                  </div>
                </div>
              )}
            </div>

            {/* Input row at the bottom */}
            <div className="chat-panel__input-row">
              <img src={DCAIcon} alt="DCA" className="chat-panel__input-avatar" />
              <div className="chat-panel__input-search">
                <SearchBox
                  isDrawer={true}
                  isKeywordSearch={isKeywordSearch}
                  query={query}
                  setQuery={setQuery}
                  setShowModal={() => {}}
                  setVisible={setVisible}
                  setCardContent={setCardContent}
                  selectedSearchHistoryId={selectedSearchHistoryId}
                  setIsGlobalSearch={setIsGlobalSearch}
                  model={routingMode === 'manual' ? selectedModel : undefined}
                  routingMode={routingMode}
                  disableCache={disableCache}
                />
              </div>
            </div>
          </Col>
        </Row>
        )}
      </Loader>
    </Drawer>
  );
};

export default DrawerOverHead;
